import React, { useState, useEffect } from "react";
import { View, TouchableOpacity, Text, StyleSheet } from "react-native";
import { normalize } from "@utils/metrics";
import { useNavigation } from "@react-navigation/native";
import { useSelector } from "react-redux";

import {
  PROFILE_EVENTS,
  PROFILE_EVENT_SOURCES,
} from "@constants/analyticsEventsCT";
import analytics from "@services/analytics";

import t from "@constants/localization";
import ChevronRight from "@assets/svg/ChevronRight";
import { getDropDownOptions } from "@services/api";
import useLeaderState from "@hooks/useLeaderState";
import HeaderSection from "./HeaderSection";
import AddressSection from "./AddressSection";
import FloatingTextLabel from "./FloatingTextLabel";

const PATH = "components/Profile/BasicInfo";

export default function BasicInfo(props) {
  const navigation = useNavigation();
  const [visible, setVisible] = useState(false);
  const { canUseFeature } = useLeaderState();
  const userDetails = useSelector(({ auth: { user } }) => user);
  const { phone_number, name, position = null } = userDetails;

  const navigateToEditDetails = () => {
    navigation.navigate("EditBasicInfo");
    analytics.trackEvent(PROFILE_EVENTS.PROFILE_EDIT, {
      section: PROFILE_EVENT_SOURCES.OTHERS,
    });
  };

  const navigateToClProfile = (key) => {
    navigation.navigate("ClProfile", {
      key,
      source: "profile",
    });
  };

  return (
    <View
      style={{
        flex: 1,
        paddingBottom: 20,
      }}
    >
      <View style={{ alignItems: "center" }}>
        <HeaderSection />
      </View>
      <FloatingTextLabel
        label={t(`${PATH}/name`)}
        value={name}
        isEditable={true}
        onEdit={() => {
          navigation.navigate("EditProfile", {
            edit_type: "Name",
          });
          analytics.trackEvent(PROFILE_EVENTS.PROFILE_EDIT, {
            section: PROFILE_EVENT_SOURCES.PRIMARY,
          });
        }}
      />
      <FloatingTextLabel label={t(`${PATH}/phoneNo`)} value={phone_number} />
      <AddressSection />
      <InfoContainer
        navigateToEditDetails={navigateToEditDetails}
        navigateToClProfile={navigateToClProfile}
        style={{ marginTop: normalize(10) }}
      />
      <MoreOption
        title={t(`${PATH}/deleteAccount`)}
        isEnabled={canUseFeature()}
        onPress={() => {
          setVisible(true);
        }}
        btnContainerStyle={{ marginBottom: 8 }}
      />
    </View>
  );
}

const InfoContainer = ({
  style,
  navigateToEditDetails,
  navigateToClProfile,
}) => {
  const userDetails = useSelector(({ auth: { user } }) => user);

  const [clDropDownOptions, setClDropDownOptions] = useState(
    userDetails?.clDropDownOptions,
  );

  useEffect(() => {
    fetchCLDropDownOptions();
  }, []);

  const fetchCLDropDownOptions = async () => {
    try {
      const { data } = await getDropDownOptions();
      setClDropDownOptions(data?.clDropDownOptions);
    } catch (error) {
      console.log(error);
    }
  };

  const getValues = (field, value) => {
    if (
      field?.component === "CustomDropDownInput" ||
      field?.component === "CustomWheelPicker"
    ) {
      const filtered = field?.options?.filter((option) => {
        if (field.name === "age") {
          if (option.value === parseInt(value)) {
            return option.key;
          }
        } else {
          if (option.value === value) {
            return option.key;
          }
        }
      });
      return filtered?.[0]?.key;
    } else {
      return value;
    }
  };
  return (
    <TouchableOpacity
      onPress={navigateToEditDetails}
      activeOpacity={1}
      style={[
        {
          backgroundColor: "white",
        },
        style,
      ]}
    >
      {clDropDownOptions
        ?.filter((field) => field.show_profile)
        ?.map((field, idx, arr) => {
          return (
            <InfoField
              key={idx}
              originalKey={field.name}
              placeholder={field.label}
              navigateToClProfile={navigateToClProfile}
              value={getValues(field, userDetails[field.name])}
              isLast={idx === arr.length - 1}
            />
          );
        })}
    </TouchableOpacity>
  );
};

const InfoField = ({
  originalKey,
  value,
  placeholder,
  navigateToClProfile,
  isLast,
}) => {
  return (
    <TouchableOpacity
      onPress={() => navigateToClProfile(originalKey, value)}
      style={{
        borderBottomColor: isLast ? "#ffffff" : "rgba(0, 0, 0, 0.1)",
        borderBottomWidth: 1,
        paddingVertical: normalize(14),
        marginHorizontal: normalize(16),
        flexDirection: "row",
        alignItems: "center",
        flex: 1,
      }}
    >
      <Text
        bold
        ellipsizeMode="tail"
        numberOfLines={1}
        style={{
          fontSize: normalize(16),
          lineHeight: normalize(24),
          color: "rgba(0, 0, 0, 0.86)",
          flex: 1,
          paddingRight: 12,
          fontFamily: "NotoSansDevanagari-SemiBold",
        }}
      >
        {placeholder}
      </Text>
      <View
        style={{
          paddingRight: normalize(12),
        }}
      >
        {value ? (
          <Text ellipsizeMode="tail" style={styles.text2}>
            {value}
          </Text>
        ) : (
          <Text style={styles.text3}>{t(`${PATH}/add`)}</Text>
        )}
      </View>
      <View style={styles.icon}>
        <ChevronRight pathFill="rgba(0, 0, 0, 0.56)" />
      </View>
    </TouchableOpacity>
  );
};

const MoreOption = ({ btnContainerStyle, onPress, title, isEnabled }) => {
  if (!isEnabled) {
    return null;
  }
  return (
    <View style={styles.btnContainer}>
      <View style={[styles.deactivationButton, btnContainerStyle]}>
        <TouchableOpacity style={styles.btn} onPress={onPress}>
          <Text style={styles.placeholderText}>{title}</Text>
          <View style={styles.icon}>
            <ChevronRight pathFill="rgba(0, 0, 0, 0.56)" />
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  btn: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  deactivationButton: {
    width: "100%",
    marginTop: 8,
    padding: 16,
    backgroundColor: "white",
  },
  text2: {
    fontSize: normalize(15),
    lineHeight: normalize(20),
    color: "rgba(0, 0, 0, 0.56)",
    marginTop: normalize(2),
    fontFamily: "NotoSansDevanagari-Regular",
  },
  text3: {
    fontSize: normalize(15),
    lineHeight: normalize(20),
    color: "#E40046",
    fontFamily: "NotoSansDevanagari-Regular",
  },
  placeholderText: {
    fontSize: normalize(16),
    lineHeight: normalize(24),
    color: "rgba(0, 0, 0, 0.86)",
    flex: 1,
    paddingRight: 12,
    fontFamily: "NotoSansDevanagari-SemiBold",
  },
  icon: { height: 18, width: 18 },
  btnContainer: { marginTop: -2, backgroundColor: "#E5E5E5" },
});
